// Select, Checkbox and Switch (DESIGN_SYSTEM §9.2). Select is the native element styled to the
// system (the searchable combobox with Devanagari/romanised matching arrives with SEARCH-001).
// Checkbox and switch hit areas are ≥ 44 px even when the box is 20 px. A switch is only for settings
// that apply immediately; otherwise use a checkbox + Save.
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import { isBlocked, type Availability } from "../state";
import { useFieldContext } from "./Field";
import { Icon } from "./Icon";
import { controlFrame } from "./Input";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  options: SelectOption[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, id, ...rest },
  ref,
) {
  const field = useFieldContext();
  const blocked = isBlocked(field?.availability);
  return (
    <div
      className={cx(
        controlFrame,
        "relative",
        field?.invalid ? "border-danger-fg" : "border-control",
        blocked && "border-subtle bg-sunken text-fg-disabled",
      )}
    >
      <select
        ref={ref}
        id={id ?? field?.id}
        aria-invalid={field?.invalid || undefined}
        aria-describedby={field?.describedBy}
        aria-disabled={blocked || undefined}
        {...rest}
        className={cx(
          "min-h-11 w-full appearance-none bg-transparent py-2.5 ps-3 pe-10 text-md outline-none lg:min-h-control lg:py-1.5 lg:text-sm",
          className,
        )}
        onChange={blocked ? undefined : rest.onChange}
      >
        {placeholder !== undefined ? (
          <option value="" disabled>
            {placeholder || t("select.placeholder")}
          </option>
        ) : null}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon
        name="chevronDown"
        className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-fg-secondary"
      />
    </div>
  );
});

interface ToggleBase {
  label: ReactNode;
  description?: ReactNode;
  availability?: Availability;
  error?: ReactNode;
}

export type CheckboxProps = ToggleBase & Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, availability, error, className, id, onChange, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? `c${auto}`;
  const blocked = isBlocked(availability);
  const descId = `${inputId}-d`;
  const hasDesc = Boolean(description || error || blocked);
  return (
    <div className={cx("flex min-h-11 items-start gap-3 py-1", className)}>
      <span className="relative flex size-11 flex-none items-center justify-center -ms-3 -my-1">
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type="checkbox"
          aria-disabled={blocked || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={hasDesc ? descId : undefined}
          onChange={blocked ? (e) => e.preventDefault() : onChange}
          onClick={blocked ? (e) => e.preventDefault() : rest.onClick}
          className={cx(
            "peer size-5 cursor-pointer appearance-none rounded-xs border bg-surface",
            "checked:border-primary checked:bg-primary",
            error ? "border-danger-fg" : "border-control",
            blocked && "cursor-not-allowed border-subtle bg-sunken",
          )}
        />
        <Icon
          name="check"
          size={16}
          className="pointer-events-none absolute text-on-primary opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="flex flex-col pt-2.5">
        <label
          htmlFor={inputId}
          className={cx("text-md leading-6", blocked ? "text-fg-disabled" : "text-fg")}
        >
          {label}
        </label>
        {hasDesc ? (
          <span
            id={descId}
            className={cx("text-xs", error ? "text-danger-fg" : "text-fg-secondary")}
          >
            {error ?? (blocked ? availability.reason : description)}
          </span>
        ) : null}
      </span>
    </div>
  );
});

export interface SwitchProps extends ToggleBase {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  /** Saving state while the immediate setting is written. */
  pending?: boolean;
  id?: string;
  className?: string;
  "data-testid"?: string;
}

export function Switch({
  label,
  description,
  availability,
  checked,
  onCheckedChange,
  pending,
  id,
  className,
  "data-testid": testId,
}: SwitchProps) {
  const auto = useId();
  const switchId = id ?? `s${auto}`;
  const blocked = isBlocked(availability);
  const descId = `${switchId}-d`;
  const hasDesc = Boolean(description || blocked);
  return (
    <div className={cx("flex min-h-11 items-center justify-between gap-4", className)}>
      <span className="flex flex-col">
        <label
          htmlFor={switchId}
          className={cx("text-md", blocked ? "text-fg-disabled" : "text-fg")}
        >
          {label}
        </label>
        {hasDesc ? (
          <span id={descId} className="text-xs text-fg-secondary">
            {blocked ? availability.reason : description}
          </span>
        ) : null}
      </span>
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={blocked || pending || undefined}
        aria-busy={pending || undefined}
        aria-describedby={hasDesc ? descId : undefined}
        data-testid={testId}
        onClick={() => {
          if (!blocked && !pending) onCheckedChange(!checked);
        }}
        className="flex min-h-11 min-w-14 flex-none items-center justify-center"
      >
        <span
          className={cx(
            "relative inline-flex h-7 w-12 items-center rounded-full border transition-colors duration-(--ady-dur-instant)",
            checked ? "border-primary bg-primary" : "border-control bg-sunken",
            blocked && "border-subtle opacity-100",
          )}
        >
          <span
            className={cx(
              "absolute size-5 rounded-full bg-surface shadow-1 transition-[inset-inline-start] duration-(--ady-dur-instant)",
              checked ? "start-6" : "start-1",
            )}
          />
        </span>
      </button>
    </div>
  );
}
